// The SHAPE of the seams, declared here rather than imported: the module depends on no client and
// on no Zod. Structurally identical to api-client's parsed types.

export type QrBatchStatus = 'PENDING' | 'DONE' | 'FAILED';

export interface QrBatch {
  id: string;
  // LOCK1 today; the 2022 batches are CBX10 (5-character codes). Printed, never switched on.
  lockModel: string;
  qrAmount: number;
  status: QrBatchStatus;
  // ISO-8601; the screen picks the zone it renders in.
  createdAt: string;
  finishedAt: string | null;
  hasFile: boolean;
  inUse: number;
  inUsePercentage: number;
}

export interface QrBatchList {
  // Newest first, as the server orders them.
  items: QrBatch[];
  hasPending: boolean;
}

// The part of the code-space stats the strip shows.
export interface QrBatchStats {
  generated: number;
  inUse: number;
  remaining: number;
  spaceUsedPercentage: number;
}

// What a request can come back with without being a failure: another batch already generating, or
// an amount the server refused. The host maps the wire codes; the slice never sees one.
export type RequestQrBatchOutcome =
  | { kind: 'requested' }
  | { kind: 'pending_exists' }
  | { kind: 'invalid_amount' };

// The four seams the host injects.
export interface QrBatchSeams {
  fetchQrBatches: () => Promise<QrBatchList>;
  fetchQrBatchStats: () => Promise<QrBatchStats>;
  requestQrBatch: (qrAmount: number) => Promise<RequestQrBatchOutcome>;
  // Fetches the batch's CSV with the session's token and hands it to the browser as a download.
  downloadQrBatch: (id: string) => Promise<void>;
}

// While a batch is generating, the list (and the stats) refresh at this pace; never otherwise.
export const POLL_INTERVAL_MS = 8_000;

export function isGenerating(list: QrBatchList | undefined): boolean {
  if (list === undefined) return false;
  return list.hasPending || list.items.some((batch) => batch.status === 'PENDING');
}
